import clsx from 'clsx';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { Fragment } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { SortIcon } from '@/core/SortIcon.atom';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { SampleInput } from '@/tools/manualSignal/SampleInput.molecule';
import { Icon } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export type InputTableColumn = {
  headerKey: string;
  type: 'dateTime' | 'value';
  name: string; // the store property used to access the column
};

export type InputTableSort = { asc: boolean; property: string };

type InputTableRow = {
  id?: string;
  dateTime: moment.MomentInput;
  value: string | number;
};

export interface InputTableIF extends ValidatingFormComponent<InputTableRow[]> {
  component: 'InputTableFormComponent';
  setEditingId: (editingId: string) => void;
  editingId: string | undefined;
  removeRow: (id: string) => void;
  timezone: {
    name: string;
    displayName: string;
    offset: string;
    offsetMinutes: number;
  };
  inputType: 'sample' | 'capsule';
  columns: [InputTableColumn, InputTableColumn];
  sort: InputTableSort;
  updateSort: (sort: InputTableSort) => void;
  hasValueTypeError?: boolean;
  isStringSignal?: boolean;
}

const InputTableFormComponentUnwrapped = (props: InputTableIF) => {
  const {
    name,
    removeRow,
    extraClassNames,
    timezone,
    setEditingId,
    editingId,
    validation,
    extendValidation,
    inputType,
    hasValueTypeError,
    columns,
    sort,
    updateSort,
    isStringSignal = false,
    value, // value is the array of rows for the table
  } = props;
  const { t } = useTranslation();
  const { use24HourFormat } = useFluxPaths(sqWorkbenchStore, ['use24HourFormat', 'dateFormat']);

  const rows = value ?? [];

  const defaultValidation = (inputValue: InputTableRow[] | undefined) => (inputValue?.length ?? 0) === 0;

  const formatSample = (sample: InputTableRow) =>
    isStringSignal ? _.toUpper(String(sample.value)) : _.toNumber(sample.value);

  const sortedRows = _.orderBy(
    rows,
    (sample: InputTableRow) => {
      return sort.property === 'dateTime' ? moment(sample.dateTime) : formatSample(sample);
    },
    [sort.asc ? 'asc' : 'desc'],
  );

  const adjustSort = (property: string) => {
    const asc = property === sort.property ? !sort.asc : true;
    updateSort({ asc, property });
  };

  const renderCell = (column: InputTableColumn, row: InputTableRow) => {
    if (column.type === 'dateTime') {
      return (
        <p className="mb0 pt1 pb1 width-115" key={row.id ?? 'row'}>
          {moment.utc(row.dateTime).tz(timezone.name).format(getPreferredTimeFormat(use24HourFormat))}
        </p>
      );
    } else {
      const cellValue = (row as Record<string, unknown>)[column.name];
      return (
        <p
          key={row.id ?? 'row'}
          className={clsx('mb0 pt1 pb1 width-75 aggressiveWordBreak', {
            'sq-text-danger': hasValueTypeError && _.isNaN(_.toNumber(cellValue)),
          })}>
          {String(cellValue ?? '')}
        </p>
      );
    }
  };

  return (
    <Field<InputTableRow[]>
      name={name}
      validate={getValidationFunction(defaultValidation, extendValidation, validation)}>
      {() => (
        <>
          {/* Input to add new rows */}
          {inputType === 'sample' && <SampleInput timezone={timezone} testId="newSampleInput" />}

          {sortedRows.length > 0 && (
            <div className="overflowYAuto max-height-800 overflowXHidden mt10">
              <table
                id={name}
                className={clsx('table table-striped table-condensed mb10 fixedHeaderTable', extraClassNames)}
                data-testid={name}>
                <thead>
                  <tr>
                    {_.map(columns, (column) => (
                      <th onClick={() => adjustSort(column.name)} key={column.name}>
                        {t(column.headerKey)}
                        <SortIcon sortAsc={sort.asc} sortProperty={column.name} sortBy={sort.property} />
                      </th>
                    ))}
                    <th />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {_.map(sortedRows, (row) => (
                    <Fragment key={(row.id ?? row.dateTime ?? 'row').toString()}>
                      <tr className="cursorPointer" data-testid="inputTableRow">
                        {_.map(columns, (column) => (
                          <td key={`${column.name}-${row.id}`}>{renderCell(column, row)}</td>
                        ))}
                        <td>
                          <Icon
                            icon="fa-pencil"
                            onClick={() => setEditingId((row.id ?? '').toString())}
                            testId={`editIcon-${row.id}`}
                          />
                        </td>
                        <td>
                          <Icon
                            icon="fa-remove"
                            onClick={() => removeRow((row.id ?? '').toString())}
                            tooltip={t('REMOVE')}
                            testId={`removeIcon-${row.id}`}
                          />
                        </td>
                      </tr>
                      {row.id === editingId && (
                        <tr>
                          <td colSpan={4}>
                            {inputType === 'sample' && (
                              <SampleInput sample={row} timezone={timezone} testId="editSampleInput" />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Field>
  );
};

export const InputTableFormComponent = React.memo(InputTableFormComponentUnwrapped, (prev, next) => {
  return !(
    prev.editingId !== next.editingId ||
    !_.isEqual(prev.timezone, next.timezone) ||
    !_.isEqual(prev.sort, next.sort) ||
    !_.isEqual(prev.value, next.value) ||
    prev.name !== next.name ||
    !_.isEqual(prev.columns, next.columns) ||
    prev.hasValueTypeError !== next.hasValueTypeError
  );
});
